// Signage → Grupos (/signage/grupos). Agrupa pantallas (por zona/ciudad/tipo)
// para asignarles contenido/programación en conjunto.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { sigInsert, sigList, sigSoftDelete, sigUpdate } from './shared';
import { GADGETS_DEFAULT, GadgetsEditor } from './GadgetsEditor';
import type { GadgetsCfg } from './player';

interface Grupo { id: string; codigo: string | null; nombre: string; descripcion: string | null; es_activo: boolean; gadgets: GadgetsCfg | null }

export function Grupos() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<Grupo> | null>(null);
  const [guardando, setGuardando] = useState(false);
  const { data: grupos = [] } = useQuery<Grupo[]>({
    queryKey: ['sig-grupos-full'], queryFn: () => sigList<Grupo>('grupo?select=*&deleted_at=is.null&order=nombre'),
  });
  const invalidar = () => { void qc.invalidateQueries({ queryKey: ['sig-grupos-full'] }); void qc.invalidateQueries({ queryKey: ['sig-grupos'] }); };

  const guardar = async () => {
    if (!edit?.nombre) { toast.error('El nombre es obligatorio'); return; }
    setGuardando(true);
    try {
      const body = { nombre: edit.nombre, codigo: edit.codigo || null, descripcion: edit.descripcion || null, es_activo: edit.es_activo ?? true, gadgets: edit.gadgets ?? null };
      if (edit.id) await sigUpdate('grupo', edit.id, body); else await sigInsert('grupo', body);
      setEdit(null); invalidar(); toast.success('Grupo guardado');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Layers className="w-5 h-5" /> Grupos de pantallas</h1>
        <Button className="ml-auto" onClick={() => setEdit({ es_activo: true })}><Plus className="w-4 h-4 mr-1" /> Nuevo grupo</Button>
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Grupo</th>
            <th className="py-2 px-3 text-left">Descripción</th><th className="py-2 px-3 text-left">Activo</th><th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {grupos.map((g, i) => (
              <tr key={g.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3">{g.nombre} {g.codigo && <span className="text-xs text-muted-foreground">{g.codigo}</span>}</td>
                <td className="py-2 px-3 text-muted-foreground">{g.descripcion || '—'}</td>
                <td className="py-2 px-3">{g.es_activo ? 'Sí' : 'No'}</td>
                <td className="py-2 px-3 text-right">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(g)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => sigSoftDelete('grupo', g.id).then(invalidar)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!grupos.length && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin grupos. Crea uno para asignar contenido a varias pantallas a la vez.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && (
        <Modal title={edit.id ? 'Editar grupo' : 'Nuevo grupo'} onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} autoFocus /></Campo>
              <Campo label="Código"><input className={fieldCls} value={edit.codigo ?? ''} onChange={(e) => setEdit({ ...edit, codigo: e.target.value })} /></Campo>
            </div>
            <Campo label="Descripción"><input className={fieldCls} value={edit.descripcion ?? ''} onChange={(e) => setEdit({ ...edit, descripcion: e.target.value })} /></Campo>
            <Switch checked={edit.es_activo ?? true} onChange={(v) => setEdit({ ...edit, es_activo: v })} label="Grupo activo" />
            <div className="border rounded-md p-2.5 space-y-2">
              <Switch checked={edit.gadgets != null}
                onChange={(v) => setEdit({ ...edit, gadgets: v ? { ...GADGETS_DEFAULT } : null })}
                label="Gadgets por defecto del grupo (las pantallas sin config propia los heredan)" />
              {edit.gadgets != null && (
                <GadgetsEditor value={edit.gadgets} onChange={(g) => setEdit({ ...edit, gadgets: g })} />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={() => void guardar()} isLoading={guardando}>Guardar</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
